import { subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import {
  ReportDateRangePreset,
  type ReportDateRangeFilter,
} from 'src/api/generated';
import type {
  CalendarDateRange,
  CalendarDateRangePreset,
  CalendarDateRangeSelection,
} from 'src/modules/shared/types/date-range.type';
import {
  formatCalendarDateRange,
  parseCalendarDateToken,
  toCalendarDateToken,
} from 'src/modules/shared/utils/date-range.util';
import type { ReportDateRangeTranslator } from './report-date-range';

const formatTokenInTimeZone = (date: Date, timezone: string): string =>
  formatInTimeZone(date, timezone, 'yyyy-MM-dd');

const subtractCalendarDays = (token: string, days: number): string => {
  const parsed = parseCalendarDateToken(token);

  return parsed ? toCalendarDateToken(subDays(parsed, days)) : token;
};

/** Resolves a report API preset into inclusive calendar dates. */
export const resolveReportDateRange = (
  value: ReportDateRangeFilter,
  timezone: string,
  now = new Date()
): CalendarDateRange => {
  const today = formatTokenInTimeZone(now, timezone);

  switch (value.preset) {
    case ReportDateRangePreset.TODAY:
      return { from: today, to: today };
    case ReportDateRangePreset.LAST_7_DAYS:
      return {
        from: subtractCalendarDays(today, 6),
        to: today,
      };
    case ReportDateRangePreset.THIS_MONTH:
      return { from: `${today.slice(0, 7)}-01`, to: today };
    case ReportDateRangePreset.LAST_30_DAYS:
      return {
        from: subtractCalendarDays(today, 29),
        to: today,
      };
    case ReportDateRangePreset.CUSTOM_RANGE:
      return { from: value.from, to: value.to };
    default:
      return { from: value.from, to: value.to };
  }
};

/** Builds the report shortcuts consumed by the generic picker. */
export const buildReportDateRangePresets = (
  timezone: string,
  t: ReportDateRangeTranslator,
  now = new Date()
): CalendarDateRangePreset<ReportDateRangePreset>[] => [
  {
    id: ReportDateRangePreset.TODAY,
    label: t('reports.dashboard.datePreset.today'),
    value: resolveReportDateRange(
      { preset: ReportDateRangePreset.TODAY },
      timezone,
      now
    ) as Required<CalendarDateRange>,
  },
  {
    id: ReportDateRangePreset.LAST_7_DAYS,
    label: t('reports.dashboard.datePreset.last7Days'),
    value: resolveReportDateRange(
      { preset: ReportDateRangePreset.LAST_7_DAYS },
      timezone,
      now
    ) as Required<CalendarDateRange>,
  },
  {
    id: ReportDateRangePreset.THIS_MONTH,
    label: t('reports.dashboard.datePreset.thisMonth'),
    value: resolveReportDateRange(
      { preset: ReportDateRangePreset.THIS_MONTH },
      timezone,
      now
    ) as Required<CalendarDateRange>,
  },
  {
    id: ReportDateRangePreset.LAST_30_DAYS,
    label: t('reports.dashboard.datePreset.last30Days'),
    value: resolveReportDateRange(
      { preset: ReportDateRangePreset.LAST_30_DAYS },
      timezone,
      now
    ) as Required<CalendarDateRange>,
  },
];

/** Maps an applied generic selection back into the report API contract. */
export const toReportDateRangeFilter = (
  selection: CalendarDateRangeSelection<ReportDateRangePreset>
): ReportDateRangeFilter =>
  selection.presetId &&
  selection.presetId !== ReportDateRangePreset.CUSTOM_RANGE
    ? { preset: selection.presetId }
    : {
        preset: ReportDateRangePreset.CUSTOM_RANGE,
        from: selection.value.from,
        to: selection.value.to,
      };

/** Formats the applied report range with both shortcut and concrete dates. */
export const formatReportDateRangePickerValue = (
  value: CalendarDateRange,
  presetId: ReportDateRangePreset | null,
  locale: string,
  t: ReportDateRangeTranslator
): string => {
  const concreteRange = formatCalendarDateRange(value, locale);

  if (!presetId || presetId === ReportDateRangePreset.CUSTOM_RANGE) {
    return concreteRange;
  }

  const labels: Record<
    Exclude<ReportDateRangePreset, ReportDateRangePreset.CUSTOM_RANGE>,
    string
  > = {
    [ReportDateRangePreset.TODAY]: t('reports.dashboard.datePreset.today'),
    [ReportDateRangePreset.LAST_7_DAYS]: t(
      'reports.dashboard.datePreset.last7Days'
    ),
    [ReportDateRangePreset.THIS_MONTH]: t(
      'reports.dashboard.datePreset.thisMonth'
    ),
    [ReportDateRangePreset.LAST_30_DAYS]: t(
      'reports.dashboard.datePreset.last30Days'
    ),
  };

  return `${labels[presetId]} · ${concreteRange}`;
};
